import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Badge,
  Tooltip
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import BlockIcon from '@mui/icons-material/Block';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import WarningIcon from '@mui/icons-material/Warning';
import { toast } from 'react-toastify';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
// Import the API functions
import { 
  updateTransportLine, 
  blockTransportLine, 
  unblockTransportLine, 
  updateTransportLineStatus,
  getTransportLinesValidationData 
} from '../../api';

const TransportLineList = ({ transportLines, onUpdate, onSelect }) => {
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [blockDialogOpen, setBlockDialogOpen] = useState(false);
  const [selectedLine, setSelectedLine] = useState(null);
  const [blockReason, setBlockReason] = useState('');
  const [editedLine, setEditedLine] = useState({
    name: '',
    razon_Social: '',
    alta_SAP: ''
  });
  const [incompleteDataMap, setIncompleteDataMap] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  // Fetch validation data for all transport lines in a single request
  useEffect(() => {
    const fetchValidationData = async () => {
      if (!transportLines || transportLines.length === 0) return;
      
      setIsLoading(true);
      try {
        // Use the new optimized API function to get validation data for all lines at once
        const validationData = await getTransportLinesValidationData();
        setIncompleteDataMap(validationData);
      } catch (error) {
        console.error('Error fetching validation data:', error);
        // In case of error, set an empty map
        setIncompleteDataMap({});
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchValidationData();
  }, [transportLines]);

  const handleRowClick = (line) => {
    if (onSelect) {
      onSelect(line);
    }
  };

  const handleEditClick = (line, event) => {
    if (event) {
      event.stopPropagation(); // Evitar que el evento llegue a la fila
    }
    setSelectedLine(line);
    setEditedLine({
      name: line.name,
      razon_Social: line.razon_Social,
      alta_SAP: line.alta_SAP
    });
    setEditDialogOpen(true);
  };

  const handleBlockClick = (line, event) => {
    if (event) {
      event.stopPropagation(); // Evitar que el evento llegue a la fila
    }
    setSelectedLine(line);
    setBlockDialogOpen(true);
  };

  const handleEditSubmit = async () => {
    try {
      await updateTransportLine(selectedLine.id, editedLine);
      toast.success('Línea de transporte actualizada exitosamente');
      onUpdate();
      setEditDialogOpen(false);
    } catch (error) {
      toast.error(error.message || 'Error al actualizar la línea de transporte');
    }
  };

  const handleBlockSubmit = async () => {
    try {
      if (selectedLine.blocked) {
        await unblockTransportLine(selectedLine.id);
        toast.success('Línea de transporte desbloqueada exitosamente');
      } else {
        await blockTransportLine(selectedLine.id, blockReason);
        toast.success('Línea de transporte bloqueada exitosamente');
      }
      onUpdate();
      setBlockDialogOpen(false);
      setBlockReason('');
    } catch (error) {
      toast.error(error.message || `Error al ${selectedLine.blocked ? 'desbloquear' : 'bloquear'} la línea de transporte`);
    }
  };

  const handleStatusChange = async (line, newStatus) => {
    try {
      await updateTransportLineStatus(line.id, newStatus);
      toast.success('Estado actualizado exitosamente');
      onUpdate();
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Sesión expirada. Por favor, inicia sesión nuevamente.');
      } else {
        toast.error(error.message || 'Error al actualizar el estado');
      }
    }
  };

  const getStatusChip = (line) => {
    if (line.authorization_status === "Por Autorizar") {
      return (
        <Chip 
          icon={<AccessTimeIcon />}
          label="Por Autorizar"
          color="warning"
        />
      );
    } else if (line.blocked) {
      return (
        <Chip 
          label="Bloqueado"
          color="error"
        />
      );
    } else if (!line.active) {
      return (
        <Chip 
          label="Pausado"
          color="warning"
        />
      );
    } else {
      return (
        <Chip 
          label="Activo"
          color="success"
        />
      );
    }
  };

  // Add sorting function
  const sortedTransportLines = [...transportLines].sort((a, b) => 
    a.name.localeCompare(b.name, 'es', { sensitivity: 'base' })
  );

  return (
    <>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Nombre</TableCell>
              <TableCell>Razón Social</TableCell>
              <TableCell>Alta SAP</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell>Activo</TableCell>
              <TableCell>Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedTransportLines.map((line) => {
              const incompleteData = incompleteDataMap[line.id] || { total: 0 };
              
              return (
                <TableRow 
                  key={line.id} 
                  onClick={() => handleRowClick(line)} 
                  style={{ cursor: 'pointer' }}
                  hover
                >
                  <TableCell>
                    {incompleteData.total > 0 ? (
                      <Tooltip title={`${incompleteData.total} elemento(s) con datos incompletos`}>
                        <Badge badgeContent={incompleteData.total} color="error">
                          {line.name}
                        </Badge>
                      </Tooltip>
                    ) : (
                      line.name
                    )}
                  </TableCell>
                  <TableCell>{line.razon_Social}</TableCell>
                  <TableCell>{line.alta_SAP}</TableCell>
                  <TableCell>
                    {getStatusChip(line)}
                  </TableCell>
                  <TableCell>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={line.active}
                          onChange={(e) => handleStatusChange(line, e.target.checked)}
                          disabled={line.blocked || line.authorization_status === "Por Autorizar"}
                        />
                      }
                      label={line.active ? "Activo" : "Inactivo"}
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton 
                      onClick={(event) => handleEditClick(line, event)} 
                      disabled={line.blocked || line.authorization_status === "Por Autorizar"}
                    >
                      <EditIcon />
                    </IconButton>
                    {!line.blocked && line.authorization_status !== "Por Autorizar" && (
                      <IconButton onClick={(event) => handleBlockClick(line, event)}>
                        <BlockIcon color="error" />
                      </IconButton>
                    )}
                    
                    {incompleteData.total > 0 && (
                      <Tooltip title="Datos incompletos">
                        <IconButton color="warning">
                          <WarningIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)}>
        <DialogTitle>Editar Línea de Transporte</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            label="Nombre"
            fullWidth
            value={editedLine.name}
            onChange={(e) => setEditedLine({...editedLine, name: e.target.value})}
          />
          <TextField
            margin="dense"
            label="Razón Social"
            fullWidth
            value={editedLine.razon_Social}
            onChange={(e) => setEditedLine({...editedLine, razon_Social: e.target.value})}
          />
          <TextField
            margin="dense"
            label="Alta SAP"
            fullWidth
            value={editedLine.alta_SAP}
            onChange={(e) => setEditedLine({...editedLine, alta_SAP: e.target.value})}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancelar</Button>
          <Button onClick={handleEditSubmit} variant="contained" color="primary">
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Block Dialog */}
      <Dialog open={blockDialogOpen} onClose={() => setBlockDialogOpen(false)}>
        <DialogTitle>
          {selectedLine?.blocked ? 'Desbloquear' : 'Bloquear'} Línea de Transporte
        </DialogTitle>
        <DialogContent>
          {!selectedLine?.blocked && (
            <TextField
              margin="dense"
              label="Razón del bloqueo"
              fullWidth
              required
              value={blockReason}
              onChange={(e) => setBlockReason(e.target.value)}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBlockDialogOpen(false)}>Cancelar</Button>
          <Button 
            onClick={handleBlockSubmit} 
            variant="contained" 
            color={selectedLine?.blocked ? "primary" : "error"}
            disabled={!selectedLine?.blocked && !blockReason}
          >
            {selectedLine?.blocked ? 'Desbloquear' : 'Bloquear'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TransportLineList;
